import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGetExamtocChapterTopicsPublishedVideoAndFlashcardCount } from "@oneapp/data";
import {
  Header,
  Icon,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
} from "@repo/ui";
import { useTheme } from "@repo/tokens";
import { ROUTESWEB, useIsMobile } from "@repo/utils";
import { SELF_LEARN_ROUTES } from "../../../../routes";
import type { Topic } from "../../../../types/reviseTypes";
import type {
  FlashcardsLocationState,
  ReviseQuestionsReportLocationState,
} from "../flashcards/types/flashcardsRoute";
import styles from "./ReviseQuestionsReportPage.module.scss";
import { defaultSelfLearnSession } from "../../../../lib/selfLearnSession";
import { ReviseAttemptAnalysis } from "./components/ReviseAttemptAnalysis";

/** Viewports at or below this width use the floating card; tablet and desktop match the flat centered layout. */
const REPORT_TABLET_MAX_WIDTH_PX = 1024;
const REPORT_MOBILE_MAX_WIDTH_PX = 480;

const SELF_LEARN_BASE = `/${ROUTESWEB.APP.SELF_LEARN}`;
const CHAPTER_LIST_HREF = `${SELF_LEARN_BASE}/${SELF_LEARN_ROUTES.CHAPTERLIST}`;
const CHAPTER_DETAILS_HREF = `${CHAPTER_LIST_HREF}/${SELF_LEARN_ROUTES.CHAPTERDETAILS}`;

type AvatarItem = {
  id: number;
  label: string;
  onPress?: () => void;
};

export function ReviseQuestionsReportPage() {
  const { t } = useTranslation("selflearn");
  const { t: tc } = useTranslation("common");
  const { setSubjectKey } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktopLayout = !useIsMobile(REPORT_TABLET_MAX_WIDTH_PX);
  const isMobileLayout = useIsMobile(REPORT_MOBILE_MAX_WIDTH_PX);
  const examId = defaultSelfLearnSession?.examId ?? 0;
  const subtenantId = defaultSelfLearnSession?.subtenantId ?? 0;

  const state = location.state as ReviseQuestionsReportLocationState | null;

  const avatarItems = useMemo(
    (): AvatarItem[] => [
      { id: 1, label: tc("profileMenu.myProfile") },
      { id: 2, label: tc("profileMenu.switchProfile") },
      { id: 3, label: tc("profileMenu.switchProduct") },
    ],
    [tc],
  );

  useEffect(() => {
    if (!state?.chapterId) {
      navigate(-1);
    }
  }, [state?.chapterId, navigate]);

  const subjectName = state?.subjectName ?? "";

  useLayoutEffect(() => {
    if (!subjectName) return;

    setSubjectKey(subjectName);

    return () => setSubjectKey(null);
  }, [setSubjectKey, subjectName]);

  const subjectId = state?.subjectId ?? 0;
  const chapterId = state?.chapterId ?? 0;
  const chapterName = state?.chapterName ?? "";
  const topicId = state?.topicId ?? 0;
  const topicName = state?.topicName ?? "";
  const correct = state?.correct ?? 0;
  const incorrect = state?.incorrect ?? 0;
  const notAnswered = state?.notAnswered ?? 0;

  const [nextTopic, setNextTopic] = useState<Topic | null>(null);

  const {
    data: topics,
    isLoading,
    isError,
  } = useGetExamtocChapterTopicsPublishedVideoAndFlashcardCount(
    {
      chapterId,
      examId,
      subtenantId,
    },
    {
      request: {
        clientName: "cms",
        paramsSerializer: { indexes: null },
      },
      query: {
        enabled: chapterId > 0 && examId > 0,
        select: (data) =>
          ((data.data as Topic[]) ?? []).filter(
            (topic) => topic.publishedFlashcardCount !== 0,
          ),
        refetchOnMount: true,
        staleTime: 0,
      },
    },
  );

  useEffect(() => {
    if (!topics || topics.length === 0) {
      setNextTopic(null);
      return;
    }
    const currentIndex = topics.findIndex((x) => x.topicId === topicId);
    if (currentIndex === -1) {
      setNextTopic(null);
      return;
    }
    setNextTopic(topics[currentIndex + 1] ?? null);
  }, [topics, topicId]);

  const handleGoToNextTopic = () => {
    if (isLoading) return;
    if (!isError && nextTopic) {
      const flashState: FlashcardsLocationState = {
        subjectId,
        subjectName,
        chapterId,
        chapterName,
        topicId: nextTopic.topicId,
        topicName: nextTopic.topicName,
        flashcardTypeId: 1,
      };
      navigate(`/${ROUTESWEB.APP.SELF_LEARN}/${SELF_LEARN_ROUTES.FLASHCARDS}`, {
        relative: "path",
        replace: true,
        state: flashState,
      });
    } else {
      navigate(-1);
    }
  };

  const handleChapterPress = () => {
    navigate(CHAPTER_DETAILS_HREF, {
      replace: true,
      state: {
        chapterId: chapterId,
        chapterName: chapterName,
        subjectId,
        subjectName,
        initialTab: "revise",
      },
    });
    return;
  };

  const handleSubjectPress = () => {
    navigate(CHAPTER_LIST_HREF, {
      replace: true,
      state: {
        subjectId,
        subjectName,
      },
    });
    return;
  };

  const buttonText = isLoading
    ? "Loading..."
    : isError
      ? t("continue")
      : nextTopic
        ? t("goToNextTopic")
        : t("continue");

  const headerTitle = t("memoryCardReport.title");
  const subjectCrumb = state?.subjectName ?? "";
  const chapterCrumb = state?.chapterName ?? "";

  const breadcrumb = useMemo(
    () => (
      <Breadcrumb>
        <BreadcrumbItem key="learn">
          <BreadcrumbLink
            href={SELF_LEARN_BASE}
            isActive={false}
            onClick={(e) => {
              e.preventDefault();
              navigate(SELF_LEARN_BASE, { replace: true });
            }}
          >
            {t("learnText")}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator key="sep-1" />
        <BreadcrumbItem key="subject">
          <BreadcrumbLink
            href={CHAPTER_LIST_HREF}
            isActive={false}
            onClick={(e) => {
              e.preventDefault();
              handleSubjectPress();
            }}
          >
            {subjectCrumb}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator key="sep-2" />
        <BreadcrumbItem key="chapter-details">
          <BreadcrumbLink
            href={CHAPTER_DETAILS_HREF}
            isActive={false}
            onClick={(event) => {
              event.preventDefault();
              handleChapterPress();
            }}
          >
            {chapterCrumb}
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator key="sep-3" />
        <BreadcrumbItem key="revise-questions-report" isCurrentPage>
          <BreadcrumbLink isActive>{headerTitle}</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>
    ),
    [navigate, subjectCrumb, chapterCrumb, headerTitle, t],
  );

  if (!state?.chapterId) {
    return null;
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Header
          title={isDesktopLayout ? undefined : headerTitle}
          leftVariant="back"
          backIcon="circleBackBtn"
          backIconShadow={true}
          backIconWidth={48}
          backIconHeight={48}
          rightIcon={isDesktopLayout ? "userProfileIcon" : undefined}
          rightIconItems={avatarItems}
          onBackPress={() => {
            navigate(-1);
          }}
          breadcrumb={isDesktopLayout ? breadcrumb : undefined}
          titleAlign="center"
          titleColor="var(--colors-neutral-10)"
          titleSize={16}
        />
      </div>

      {isLoading ? (
        <div className={styles.mainArea}>
          <div className={styles.spinner} aria-hidden />
        </div>
      ) : isDesktopLayout ? (
        <div className={styles.innerDesktop}>
          <div className={styles.desktopScroll}>
            <div className={styles.desktopContent}>
              {topicName ? (
                <p className={styles.topicDesktop}>{topicName}</p>
              ) : null}
              <div className={styles.iconWrapDesktop}>
                <Icon name="winCup" width="100%" height="100%" />
              </div>
              <div className={styles.desktopWelcome}>
                <p className={styles.wellDoneDesktop}>
                  {t("memoryCardReport.wellDone")}
                </p>
                <p className={styles.subtitleDesktop}>
                  {t("topicSummaryCompleted")}
                </p>
              </div>
              <ReviseAttemptAnalysis
                correct={correct}
                incorrect={incorrect}
                notAnswered={notAnswered}
                variant="wide"
                isMobileLayout={isMobileLayout}
              />
            </div>
          </div>
          <div className={styles.desktopCtaBar}>
            <button
              type="button"
              className={styles.ctaDesktop}
              onClick={handleGoToNextTopic}
              disabled={isLoading}
            >
              <span className={styles.ctaLabelDesktop}>{buttonText}</span>
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.innerNarrow}>
          <div className={styles.narrowScroll}>
            <div className={styles.containerNarrow}>
              <div className={styles.cardNarrow}>
                {topicName ? (
                  <p className={styles.topicNarrow}>{topicName}</p>
                ) : null}
                <div className={styles.iconWrapNarrow}>
                  <Icon name="winCup" width={150} height={180} />
                </div>
                <p className={styles.wellDoneNarrow}>
                  {t("memoryCardReport.wellDone")}
                </p>
                <p className={styles.subtitleNarrow}>
                  {t("topicSummaryCompleted")}
                </p>
                <ReviseAttemptAnalysis
                  correct={correct}
                  incorrect={incorrect}
                  notAnswered={notAnswered}
                  variant="narrow"
                  isMobileLayout={isMobileLayout}
                />
              </div>
            </div>
          </div>
          <div className={styles.narrowCtaBar}>
            <button
              type="button"
              className={styles.ctaNarrow}
              onClick={handleGoToNextTopic}
              disabled={isLoading}
            >
              <span className={styles.ctaLabelNarrow}>{buttonText}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
